import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { EvoReedyEventType } from '../../services/firebaseFunctions';

// ── Editar nome e observações (J7) ───────────────────────────────────────────

export interface EvoProfileSubmitResult {
  success: boolean;
  error?: string;
}

interface ProfileDialogProps {
  open: boolean;
  name: string;
  notes: string;
  onClose: () => void;
  /** Salva nome e observações. O pai fecha o diálogo em caso de sucesso. */
  onSubmit: (values: { name: string; notes: string }) => Promise<EvoProfileSubmitResult>;
}

export const EvoWebhookProfileDialog: React.FC<ProfileDialogProps> = ({ open, name, notes, onClose, onSubmit }) => {
  const [nameDraft, setNameDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setNameDraft(name);
    setNotesDraft(notes);
    setSaving(false);
    setError('');
    // Só reinicia ao abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedName = nameDraft.trim();
  const dirty = trimmedName !== name.trim() || notesDraft.trim() !== notes.trim();

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await onSubmit({ name: trimmedName, notes: notesDraft.trim() });
      if (!result.success) setError(result.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="evo-profile-title">
      <DialogTitle id="evo-profile-title">Editar nome e observações</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            fullWidth
            required
            label="Nome"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            inputProps={{ maxLength: 100 }}
            helperText={`${nameDraft.length}/100`}
            disabled={saving}
          />
          <TextField
            fullWidth
            label="Observações"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            multiline
            rows={3}
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !trimmedName || !dirty}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Criar webhooks na EVO (J4) ───────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  companyName: string;
  configName: string;
  /** Eventos que serão criados (só os ausentes). */
  eventsToCreate: EvoReedyEventType[];
  /** Eventos que já existem e não serão alterados. */
  existingEvents: EvoReedyEventType[];
  nativeIntegration: boolean;
  creating: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const EventChips: React.FC<{ events: EvoReedyEventType[] }> = ({ events }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    {events.map((eventType) => (
      <Chip key={eventType} label={eventType} size="small" variant="outlined" />
    ))}
  </Box>
);

/** Confirmação de criação: só empresa, cadastro e eventos. Nunca mostra token, DNS nem callback (DoD 5). */
export const EvoWebhookCreateDialog: React.FC<CreateDialogProps> = ({
  open,
  companyName,
  configName,
  eventsToCreate,
  existingEvents,
  nativeIntegration,
  creating,
  onCancel,
  onConfirm,
}) => {
  const count = eventsToCreate.length;
  const partial = existingEvents.length > 0;
  const title = !partial ? 'Criar webhooks na EVO' : count === 1 ? 'Criar o webhook que falta' : 'Criar os webhooks que faltam';
  const intro = count === 1 ? 'Será criado 1 webhook na EVO para:' : `Serão criados ${count} webhooks na EVO para:`;
  const confirmLabel = creating ? 'Criando…' : `Criar ${count} ${count === 1 ? 'webhook' : 'webhooks'}`;

  return (
    <Dialog open={open} onClose={creating ? undefined : onCancel} maxWidth="sm" fullWidth aria-labelledby="evo-create-title">
      <DialogTitle id="evo-create-title">{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">{intro}</Typography>
          <Box
            component="dl"
            sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(80px, 120px) 1fr', gap: 1, typography: 'body2' }}
          >
            <Box component="dt" sx={{ color: 'text.secondary' }}>
              Empresa
            </Box>
            <Box component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
              {companyName}
            </Box>
            <Box component="dt" sx={{ color: 'text.secondary' }}>
              Cadastro
            </Box>
            <Box component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
              {configName}
            </Box>
          </Box>
          {partial && <Typography variant="subtitle2">Será criado</Typography>}
          <EventChips events={eventsToCreate} />
          {partial && (
            <>
              <Typography variant="subtitle2">Já existem e não serão alterados</Typography>
              <EventChips events={existingEvents} />
            </>
          )}
          {nativeIntegration && (
            <Alert severity="info" role="status">
              Token de integração nativa: as chamadas à EVO levarão o cabeçalho da integração nativa.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={creating}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={creating || count === 0}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
